/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef} from 'react'
import {
    Stack,
    Divider,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Text,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import SizeRefinements from './size-refinements'
import CheckboxRefinements from './checkbox-refinements'
import LinkRefinements from '@salesforce/retail-react-app/app/pages/product-list/partials/link-refinements'
import RangeSliderRefinements from './price-range-slider'
import {SIZE_REFINEMENTS_FALLBACK} from '../../../constants'
import {ChevronDownType2} from '../../../components/icons'
import NoFilterMatch from './no-filter-match'
import NoExactMatch from './no-exact-match'

/** Map of refinement attribute IDs to the components used to display values as filter options. */
export const componentMap = {
    cgid: LinkRefinements,
    c_size: SizeRefinements,
    c_refinementColor: CheckboxRefinements,
    price: RangeSliderRefinements
}

const Refinements = ({
    excludedFilters = [],
    filters = [],
    toggleFilter,
    selectedFilters,
    isLoading,
    initiallyExpandedFilters = [],
    sizeGrouping,
    filterRefs,
    productSearchResult,
    category
}) => {
    const refinementsStyles = useMultiStyleConfig('Refinements')
    if (excludedFilters) {
        filters = filters.filter(({attributeId}) => !excludedFilters.includes(attributeId))
    }

    const sizeAttributeIds = sizeGrouping ? sizeGrouping?.split(',') : SIZE_REFINEMENTS_FALLBACK
    const showNoFilterMatch = !isLoading && productSearchResult && !productSearchResult?.hits
    const sizeFilters = filters.filter(
        ({attributeId, values}) =>
            sizeAttributeIds.includes(attributeId) && Array.isArray(values) && values.length > 0
    )
    filters = filters.filter(({attributeId}) => !sizeAttributeIds.includes(attributeId))
    const colourIndex = filters.findIndex((f) => f.attributeId === 'c_refinementColor')

    if (colourIndex >= 0) {
        filters.splice(colourIndex + 1, 0, {
            attributeId: 'c_size',
            label: 'Size',
            values: sizeFilters
        })
    } else {
        filters.push({
            attributeId: 'c_size',
            label: 'Size',
            values: sizeFilters
        })
    }

    // Only filters that actually render an AccordionItem. Chakra tracks the
    // expanded panels by the index of the *rendered* items, so our expansion
    // indices must be built from this same list (not the raw `filters`, which
    // may contain entries that render nothing).
    const renderableFilters = filters.filter((filter) => filter.values && filter.values.length)

    // Track expansion by attributeId (not positional index) so a panel stays
    // attached to its filter when the refinement list changes (values dropping
    // to 0 hits, groups appearing/disappearing, reordering) after a selection.
    const [expandedIds, setExpandedIds] = useState(() => new Set(initiallyExpandedFilters))

    // Expand a newly-requested filter (e.g. the one the user opened from the
    // header) without collapsing what they already had open.
    const initiallyExpandedKey = initiallyExpandedFilters.filter(Boolean).join('|')
    const prevInitiallyExpandedKey = useRef(initiallyExpandedKey)
    useEffect(() => {
        if (prevInitiallyExpandedKey.current === initiallyExpandedKey) return
        prevInitiallyExpandedKey.current = initiallyExpandedKey
        setExpandedIds((prev) => {
            const next = new Set(prev)
            initiallyExpandedFilters.forEach((id) => id && next.add(id))
            return next
        })
    }, [initiallyExpandedKey])

    const expandedIndexes = renderableFilters
        .map((filter, index) => (expandedIds.has(filter.attributeId) ? index : -1))
        .filter((index) => index >= 0)

    const handleAccordionChange = (indexes) => {
        const indexArray = Array.isArray(indexes) ? indexes : [indexes]
        setExpandedIds(
            new Set(indexArray.map((i) => renderableFilters[i]?.attributeId).filter(Boolean))
        )
    }

    return (
        <Stack spacing={8} {...refinementsStyles.container}>
            {showNoFilterMatch ? (
                <NoExactMatch />
            ) : (
                /* Wait to have filters before rendering the Accordion to allow the default indexes to be accurate */
                <Accordion
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    opacity={isLoading ? 0.2 : 1}
                    allowMultiple={true}
                    index={expandedIndexes}
                    onChange={handleAccordionChange}
                    reduceMotion={false}
                    variant={'filter'}
                >
                    {renderableFilters.map((filter, idx) => {
                        const attributeId =
                            filter.attributeId === 'c_refinementColor' ? 'Colour' : filter.attributeId

                        const Values = componentMap[attributeId] || CheckboxRefinements

                        const isSizeFilter = filter.attributeId === 'c_size'

                        // For the synthetic Size filter the count must cover every size
                        // attribute (Clothing Size, Bottom Size, ...) because the user can
                        // have selections in more than one subgroup at once.
                        let selectedFiltersArray = []
                        let sizeSelectedCount = 0
                        if (isSizeFilter) {
                            sizeAttributeIds.forEach((id) => {
                                const raw = selectedFilters?.[id]
                                if (raw == null) return
                                sizeSelectedCount += Array.isArray(raw) ? raw.length : 1
                            })
                        } else {
                            selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []
                            if (!Array.isArray(selectedFiltersArray)) {
                                selectedFiltersArray = [selectedFiltersArray]
                            }
                        }

                        const hasSelectedCount = isSizeFilter
                            ? sizeSelectedCount > 0
                            : !!selectedFiltersArray.length
                        const selectedCountLabel = isSizeFilter
                            ? sizeSelectedCount
                            : selectedFiltersArray.length

                        const sizeValuesFilterExist = filter?.values?.filter(item => item.values && item.values.length > 0)?.length > 0

                        if (filter.values && filter.values.length) {
                            return (
                                <React.Fragment key={filter.attributeId}>
                                    <Stack
                                        divider={<Divider />}
                                        ref={(el) => {
                                            if (el && filterRefs) {
                                                filterRefs.current[filter.attributeId] = el
                                            }
                                        }}
                                        display={filter?.attributeId === 'c_size' && !sizeValuesFilterExist && 'none'}
                                    >
                                        <AccordionItem {...refinementsStyles.accItem}>
                                            {({isExpanded}) => (
                                                <>
                                                    <AccordionButton
                                                        data-testid={`refinement-accordion-button-${filter.attributeId}`}
                                                        paddingTop={0}
                                                        paddingBottom={isExpanded ? 2 : 0}
                                                    >
                                                        <Text {...refinementsStyles.accTitle}>
                                                            {filter.label}{' '}
                                                            {hasSelectedCount && filter.attributeId !== 'price' && (
                                                                <span>({selectedCountLabel})</span>
                                                            )}
                                                        </Text>
                                                        <ChevronDownType2
                                                            transform={
                                                                isExpanded
                                                                    ? 'rotate(180deg)'
                                                                    : 'rotate(0deg)'
                                                            }
                                                            transition="transform 0.2s"
                                                            {...refinementsStyles.accArrow}
                                                        />
                                                    </AccordionButton>
                                                    <AccordionPanel paddingLeft={0}
                                                        {...showNoFilterMatch && {
                                                            paddingBottom: 0
                                                        }}>
                                                        {filter.attributeId === 'c_size' ? (
                                                            <SizeRefinements
                                                                selectedFilters={selectedFilters}
                                                                filter={filter}
                                                                toggleFilter={toggleFilter}
                                                            />
                                                        ) : (
                                                            <Values
                                                                selectedFilters={selectedFiltersArray}
                                                                filter={filter}
                                                                toggleFilter={toggleFilter}
                                                            />
                                                        )}
                                                        {showNoFilterMatch &&
                                                            <NoFilterMatch
                                                                filter={filter}
                                                                toggleFilter={toggleFilter}
                                                                productSearchResult={productSearchResult}
                                                                category={category}
                                                                selectedFilters={selectedFilters}
                                                            />
                                                        }
                                                    </AccordionPanel>
                                                </>
                                            )}
                                        </AccordionItem>
                                    </Stack>
                                    {showNoFilterMatch &&
                                        <Divider />
                                    }
                                </React.Fragment>
                            )
                        } else {
                            return null
                        }
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

Refinements.propTypes = {
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    initiallyExpandedFilters: PropTypes.arrayOf(PropTypes.string),
    sizeGrouping: PropTypes.string,
    filterRefs: PropTypes.object,
    productSearchResult: PropTypes.object,
    category: PropTypes.object
}

export default Refinements
